import React, { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { REFUND_POLICY_CLAUSES, BUSINESS_INFO, WITHDRAWAL_WINDOW_DAYS, clauseText, clauseBasis } from '../constants/legal';

export default function AboutPage() {
  const { hash } = useLocation();
  const { i18n } = useTranslation();
  const isKo = i18n.language === 'ko';

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.replace('#', ''));
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [hash]);

  return (
    <div className="max-w-3xl mx-auto px-6 py-12" style={{ color: 'var(--text-primary)' }}>
      {/* Brand header */}
      <div className="mb-12 text-center">
        <h1 className="font-headline font-extrabold text-3xl uppercase mb-2" style={{ color: 'var(--text-primary)' }}>VERIPASS</h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>{isKo ? '화장품 규정 준수 AI 검증 서비스' : 'AI-Powered Cosmetic Compliance Verification'}</p>
      </div>

      {/* Navigation pills */}
      <div className="flex flex-wrap justify-center gap-2 mb-10">
        {['about', 'terms', 'privacy', 'refund', 'ethics'].map(id => (
          <a key={id} href={`#${id}`} className="text-xs font-bold uppercase px-3 py-1.5 rounded-full transition-colors" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: 'var(--text-secondary)' }} onMouseEnter={e => { e.currentTarget.style.borderColor = '#a100aa'; e.currentTarget.style.color = 'var(--text-primary)'; }} onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-color)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}>
            {id === 'about' ? (isKo ? '소개' : 'About') : id === 'terms' ? (isKo ? '이용약관' : 'Terms') : id === 'privacy' ? (isKo ? '개인정보' : 'Privacy') : id === 'refund' ? (isKo ? '환불' : 'Refund') : (isKo ? '윤리' : 'Ethics')}
          </a>
        ))}
      </div>

      {/* About */}
      <Section id="about" title={isKo ? '서비스 소개' : 'About VeriPass'}>
        <P>{isKo
          ? 'VeriPass는 화장품 규정 준수를 AI로 검증하는 SaaS 서비스입니다. 브랜드, 제조업체, 규제 전문가가 제품 클레임이 FDA 규정을 준수하는지 신속하게 확인할 수 있습니다.'
          : 'VeriPass is an AI-powered SaaS platform that verifies cosmetic regulatory compliance. Brands, manufacturers, and regulatory professionals can quickly check whether product claims comply with FDA regulations.'
        }</P>
        <P>{isKo
          ? '규칙 엔진, 벡터 검색, 앙상블 분류기를 결합한 다단계 분석 파이프라인을 통해 높은 정확도의 규정 준수 평가를 제공합니다.'
          : 'Our multi-stage analysis pipeline combines rule engines, vector retrieval, and ensemble classifiers to deliver highly accurate compliance assessments.'
        }</P>
        {/* 전자상거래법 제10조 disclosure. Every field comes from constants/legal.js:
            상호명 / 대표자명 / 사업자등록번호 / 소재지 / 연락처 were half literal and
            half constant here, and the literal half is how the same 대표자명 came to
            be romanised two different ways across the app. 소재지 and 연락처 were
            missing outright -- /about is the page a reviewer opens first. */}
        <div className="mt-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
          <p>{isKo ? BUSINESS_INFO.nameKo : BUSINESS_INFO.nameEn} | {isKo ? '대표' : 'CEO'}: {isKo ? BUSINESS_INFO.ceoKo : BUSINESS_INFO.ceoEn}</p>
          <p>{isKo ? '사업자등록번호' : 'Business Registration'}: {BUSINESS_INFO.bizRegNo}</p>
          {/* T0.4 — 전자상거래법 제10조 disclosure. The value is ECOMMERCE_FILING_NO
              in constants/legal.js (supplied 2026-10-02). Still guarded: a null
              renders nothing, and a placeholder is never substituted -- on a page
              that sits behind a payment flow it would read as verified. */}
          {BUSINESS_INFO.ecommerceFilingNo && (
            <p>{isKo ? '통신판매업 신고번호' : 'E-Commerce Filing No.'}: {BUSINESS_INFO.ecommerceFilingNo}</p>
          )}
          <p>{isKo ? '사업자 소재지' : 'Address'}: {isKo ? BUSINESS_INFO.addressKo : BUSINESS_INFO.addressEn}</p>
          <p>{isKo ? '연락처' : 'Tel'}: {BUSINESS_INFO.tel}</p>
          <p>{isKo ? '이메일' : 'Email'}: hello@whereat.kr</p>
        </div>
      </Section>

      {/* Terms of Service */}
      <Section id="terms" title={isKo ? '이용약관' : 'Terms of Service'}>
        {/* The terms carried no date until §5 was reworded (KG이니시스 review,
            2026-10-02); same format as the refund policy's date below.
            Copy status: pending_ceo (D9).
            ⚠ 2026-10-03 is the date this was written, not a confirmed deploy
            date: set this and the refund policy date to the real one if it
            differs. */}
        <p className="text-xs mb-4" style={{ color: 'var(--text-secondary)' }}>
          {isKo ? '최종 업데이트: 2026년 10월 3일' : 'Last Updated: October 3, 2026'}
        </p>
        <Sub>{isKo ? '1. 약관의 수락' : '1. Acceptance of Terms'}</Sub>
        <P>{isKo ? 'VeriPass에 접속하고 사용함으로써 본 약관의 조건에 동의하게 됩니다.' : 'By accessing and using VeriPass, you accept and agree to be bound by these terms.'}</P>
        <Sub>{isKo ? '2. 사용 허가' : '2. Use License'}</Sub>
        <P>{isKo ? '규정 준수 분석 목적으로 VeriPass를 사용할 수 있는 권한이 부여됩니다. 본 약관을 위반할 경우 라이선스는 자동으로 종료됩니다.' : 'Permission is granted to use VeriPass for regulatory compliance analysis. This license terminates automatically if you violate any restrictions.'}</P>
        <Sub>{isKo ? '3. 면책 조항' : '3. Disclaimer'}</Sub>
        <P>{isKo ? 'VeriPass는 분석 도구와 정보를 제공하지만 법적 또는 규제 자문을 구성하지 않습니다. 모든 분석 결과는 자격을 갖춘 규제 전문가가 검토해야 합니다.' : 'VeriPass provides analysis tools and information but does not constitute legal or regulatory advice. All results should be reviewed by qualified regulatory professionals.'}</P>
        <Sub>{isKo ? '4. 책임 제한' : '4. Limitation of Liability'}</Sub>
        <P>{isKo ? 'VeriPass 또는 그 공급업체는 VeriPass의 사용 또는 사용 불능으로 인해 발생하는 어떠한 손해에 대해서도 책임을 지지 않습니다.' : 'VeriPass shall not be liable for any damages arising from use or inability to use the platform.'}</P>

        {/* T0.4 — clauses 5–7 did not exist. The 이용약관 had NO cancellation,
            refund or withdrawal term at all, while four other surfaces published
            one. The terms are the document a reviewer or a court reads first, so
            silence here was the most expensive silence we had. */}
        {/* §5 describes a SERVICE with a fixed quantity (분석 리포트 N건 제공),
            never credits as the means of payment: KG이니시스 classifies credits
            as 환금성 (cash-equivalent). Wording rules: the header of
            REFUND_POLICY_CLAUSES in constants/legal.js. */}
        <Sub>{isKo ? '5. 요금제 및 결제' : '5. Plans and Payment'}</Sub>
        <P>{isKo
          ? '모든 유료 요금제는 12개월 이용 상품입니다. 결제일에 12개월 이용료 전액이 한 번에 결제되며, 요금제에 포함된 분석 리포트는 이용 기간 동안 매월 제공됩니다. 자동 갱신은 없으며, 기간이 끝나면 무료 플랜으로 전환됩니다.'
          : 'All paid plans are 12-month service plans. The full 12-month fee is charged once on the purchase date, and the analysis reports included in your plan are provided each month during the term. There is no automatic renewal; at the end of the term the account returns to the free tier.'}</P>
        <Sub>{isKo ? '6. 청약철회 및 중도 해지' : '6. Withdrawal and Mid-Term Termination'}</Sub>
        <P>{isKo
          ? `결제일로부터 ${WITHDRAWAL_WINDOW_DAYS}일 이내에 청약철회하실 수 있습니다. 서비스 이용을 시작하신 경우에는 이미 제공된 부분을 제외한 나머지 기간에 대해 철회하실 수 있으며, 이용 기간 중에는 사유와 관계없이 언제든지 중도 해지를 요청하실 수 있습니다. 자세한 산정 기준은 아래 환불 및 청약철회 정책을 따릅니다.`
          : `You may withdraw within ${WITHDRAWAL_WINDOW_DAYS} days of purchase. Once you have begun using the service, withdrawal covers the portion not yet supplied, and during the term you may request mid-term termination at any time and for any reason. Amounts are calculated under the Refund and Withdrawal Policy below.`}</P>
        <Sub>{isKo ? '7. 환불' : '7. Refunds'}</Sub>
        <P>{isKo
          ? '환불액과 절차는 본 문서의 ‘환불 및 청약철회 정책’이 정하는 바에 따르며, 해당 정책은 본 약관의 일부를 구성합니다. 중도 해지에 따른 위약금은 청구하지 않습니다.'
          : 'Refund amounts and procedure are governed by the Refund and Withdrawal Policy in this document, which forms part of these terms. No cancellation penalty is charged.'}</P>
      </Section>

      {/* Privacy Policy */}
      <Section id="privacy" title={isKo ? '개인정보처리방침' : 'Privacy Policy'}>
        <Sub>{isKo ? '수집하는 정보' : 'Information We Collect'}</Sub>
        <P>{isKo ? '이름, 이메일 주소, 조직명, 분석을 위해 제출된 제품 클레임 등의 정보를 수집합니다.' : 'We collect name, email, organization name, and product claims submitted for analysis.'}</P>
        <Sub>{isKo ? '정보 사용 방법' : 'How We Use Your Information'}</Sub>
        <P>{isKo ? '서비스 제공, 유지 및 개선, 분석 요청 처리, 서비스 관련 소통을 위해 수집된 정보를 사용합니다.' : 'We use collected information to provide, maintain, and improve VeriPass, process analysis requests, and communicate about our services.'}</P>
        <Sub>{isKo ? '장치 추적' : 'Device Tracking'}</Sub>
        <P>{isKo ? '보안 및 부정행위 방지를 위해 동의한 사용자의 장치 정보를 수집합니다. AES-256 암호화로 보호되며 제3자와 공유하지 않습니다. 수집된 데이터는 1년간 보관됩니다.' : 'With consent, we collect device information for security and fraud prevention. Data is protected by AES-256 encryption, never shared with third parties, and retained for 1 year.'}</P>
        <Sub>{isKo ? '데이터 보안' : 'Data Security'}</Sub>
        <P>{isKo ? '무단 접근, 공개, 변경 및 파기로부터 정보를 보호하기 위해 업계 표준 보안 조치를 사용합니다.' : 'We use industry-standard security measures to protect your information from unauthorized access, disclosure, alteration, and destruction.'}</P>
      </Section>

      {/* Refund Policy.
          T0.4/P5 — the clause list is RENDERED from constants/legal.js, not
          written here. This page used to carry its own wording, which is how it
          came to disagree with the subscription-cancelled email. Do not inline
          refund text into this file again; edit the constant. */}
      <Section id="refund" title={isKo ? '환불 및 청약철회 정책' : 'Refund and Withdrawal Policy'}>
        {/* ⚠ 2026-10-03 is the date this was written, not a confirmed deploy
            date (KG이니시스 rewording); set it, and the terms date above, to
            the real one if it differs. */}
        <p className="text-xs mb-4" style={{ color: 'var(--text-secondary)' }}>
          {isKo ? '최종 업데이트: 2026년 10월 3일' : 'Last Updated: October 3, 2026'}
        </p>
        <ol className="space-y-3 text-sm leading-relaxed" style={{ color: 'var(--text-secondary)', listStyle: 'decimal', paddingLeft: '1.25rem' }}>
          {REFUND_POLICY_CLAUSES.map(c => (
            <li key={c.id}>
              {clauseText(c, isKo)}
              {clauseBasis(c, isKo) && (
                <span className="block text-xs mt-1" style={{ opacity: 0.7 }}>
                  ({clauseBasis(c, isKo)})
                </span>
              )}
            </li>
          ))}
        </ol>
      </Section>

      {/* Ethics */}
      <Section id="ethics" title={isKo ? '윤리 가이드라인' : 'Ethics Guidelines'}>
        <P>{isKo
          ? 'VeriPass는 투명하고 책임감 있는 AI 사용을 위해 노력합니다. 분석 결과는 참고용이며 최종 판단은 자격 있는 전문가가 내려야 합니다.'
          : 'VeriPass is committed to transparent and responsible AI use. Analysis results are for reference only; final decisions should be made by qualified professionals.'
        }</P>
        <P>{isKo
          ? '우리는 데이터 프라이버시를 존중하며, 분석을 위해 제출된 데이터를 AI 모델 학습에 사용하지 않습니다.'
          : 'We respect data privacy and do not use data submitted for analysis to train AI models.'
        }</P>
      </Section>

      {/* Back link */}
      <div className="text-center mt-12 pb-8">
        <Link to="/" className="text-sm transition-colors" style={{ color: 'var(--text-secondary)' }} onMouseEnter={e => e.target.style.color = 'var(--text-primary)'} onMouseLeave={e => e.target.style.color = 'var(--text-secondary)'}>
          {isKo ? '← 홈으로 돌아가기' : '← Return to Home'}
        </Link>
      </div>
    </div>
  );
}

function Section({ id, title, children }) {
  return (
    <section id={id} className="mb-10 scroll-mt-20" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', borderRadius: 16, padding: '28px 28px 20px' }}>
      <h2 className="font-headline font-bold text-lg mb-4" style={{ color: 'var(--text-primary)' }}>{title}</h2>
      {children}
    </section>
  );
}

function Sub({ children }) {
  return <h3 className="font-semibold text-sm mt-4 mb-1.5" style={{ color: 'var(--text-primary)' }}>{children}</h3>;
}

function P({ children }) {
  return <p className="text-sm leading-relaxed mb-3" style={{ color: 'var(--text-secondary)' }}>{children}</p>;
}
